import { useMemo } from 'react';
import type { Column } from '../../Table/Table';
import styled from 'styled-components';
import { NamedContentEntryText } from '../../ContentEntryText/NamedContentEntryText';
import { ScoreBreakdownType, type TeamBehavior } from '../../../lib/types/TeamBehavior';
import { BehaviorBar } from '../components/BehaviorBar/BehaviorBar';
import { BehaviorTypeHeader } from '../components/BehaviorTypeHeader/BehaviorTypeHeader';
import { BehaviorType } from '../enum/BehaviorType';
import getTeamBehaviorDetails from '../utils/getTeamBehaviorDetails';
import { NudgeOptionMenu } from '../components/NudgeBehavior/NudgeOptionMenu';
import type { NudgeModalConfigs } from '../../Nudge/NudgeModal';

type TeamBehaviorsData = {
  columns: Column<TeamBehavior>[];
};

export const useTeamBehaviorsData = (
  totalCount: number,
  setNudgeModalConfigs: (modalProps: NudgeModalConfigs) => void,
  onClickBehavior: (behavior: ScoreBreakdownType, type: BehaviorType) => void,
): TeamBehaviorsData => {
  return useMemo(
    () => ({
      columns: [
        {
          header: (
            <HeaderTitle>
              <BehaviorTypeContainer>
                <BehaviorTypeHeader type={BehaviorType.WEAK} />
                <BehaviorTypeHeader type={BehaviorType.NEUTRAL} />
                <BehaviorTypeHeader type={BehaviorType.STRONG} />
              </BehaviorTypeContainer>
            </HeaderTitle>
          ),
          valueSelector: (teamBehaviorDetails: TeamBehavior) => (
            <BehaviorBar totalCount={totalCount} teamBehaviorDetails={teamBehaviorDetails} onClick={onClickBehavior} />
          ),
          alignment: 'left',
          width: '8fr',
        },
        {
          header: (
            <HeaderTitle>
              <NamedContentEntryText container={'span'} refKey="managerTeamBehaviors" subKey="behaviors" />
            </HeaderTitle>
          ),
          valueSelector: (teamBehaviorDetails: TeamBehavior) => {
            const mappingBehaviorDetails = getTeamBehaviorDetails(teamBehaviorDetails.behaviour);
            return (
              <BehaviorTypeDetails>
                {mappingBehaviorDetails?.icon}
                <span>{mappingBehaviorDetails?.displayName}</span>
              </BehaviorTypeDetails>
            );
          },
          alignment: 'left',
          width: '6fr',
        },
        {
          header: (
            <HeaderTitle>
              <NamedContentEntryText container={'span'} refKey="managerTeamBehaviors" subKey="action" />{' '}
            </HeaderTitle>
          ),
          valueSelector: (teamBehaviorDetails: TeamBehavior) => (
            <NudgeOptionMenu
              behavior={teamBehaviorDetails.behaviour}
              learners={teamBehaviorDetails.learners}
              setNudgeModalConfigs={setNudgeModalConfigs}
              hideMenu={teamBehaviorDetails.learners.strong === 0 && teamBehaviorDetails.learners.weak === 0}
            />
          ),
          alignment: 'right',
          width: '50px',
        },
      ],
    }),
    [totalCount, onClickBehavior, setNudgeModalConfigs],
  );
};

const BehaviorTypeContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const HeaderTitle = styled.div`
  width: 100%;
  font-weight: 400;
  font-size: 12px;
`;

const BehaviorTypeDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
